import { formatUserFacingError } from '@/lib/errors'
import { useState, useEffect } from 'react'
import { RefreshCw, Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { syncPrintifyProductsFn } from '@/lib/firebase/functions'
import { subscribeToSyncMeta } from '../services/shop.service'
import type { ShopSyncMetaDocument } from '../types'

export function AdminShopPage() {
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [syncResult, setSyncResult] = useState<{ productCount: number } | null>(null)
  const [meta, setMeta] = useState<ShopSyncMetaDocument | null>(null)

  useEffect(() => {
    const unsubscribe = subscribeToSyncMeta(setMeta)
    return unsubscribe
  }, [])

  async function handleSync() {
    setSyncing(true)
    setError(null)
    setSyncResult(null)
    try {
      const result = await syncPrintifyProductsFn({})
      setSyncResult({ productCount: result.data.productCount })
    } catch (err: unknown) {
      setError(formatUserFacingError(err, 'Sync failed.'))
    } finally {
      setSyncing(false)
    }
  }

  const lastSynced = meta?.lastSyncedAt
    ? new Date((meta.lastSyncedAt as { toDate: () => Date }).toDate()).toLocaleString()
    : null

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Shop</h1>
        <p className="text-muted-foreground">Manage your Printify merch store.</p>
      </div>

      <Card className="max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Product Sync
          </CardTitle>
          <CardDescription>
            Pull the latest products from your Printify account into Firestore.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {meta && (
            <div className="rounded-md bg-muted p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Products synced</span>
                <span className="font-medium">{meta.productCount}</span>
              </div>
              {lastSynced && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last synced</span>
                  <span className="font-medium">{lastSynced}</span>
                </div>
              )}
            </div>
          )}

          {syncResult && (
            <p className="text-sm text-green-600">
              Sync complete — {syncResult.productCount} product
              {syncResult.productCount !== 1 ? 's' : ''} loaded.
            </p>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button onClick={handleSync} disabled={syncing} className="w-full">
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync from Printify'}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
